import { CalendarApi, DateSelectArg } from '@fullcalendar/core';
import {
  Goal,
  ID,
  LifeArea,
  LinkOptions,
  SmartList,
  Tag,
  TaskActionType,
  Timeslot,
} from '@griply/types';
import {
  getCalendarTaskDuration,
  getCalendarTaskStartTime,
  resolveTimeslotLink,
} from '@griply/utils';
import { endOfDay } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import { useOpenCreateEventPopup } from 'shared/contexts/event-form';
import { useOpenCreateTask } from 'shared/contexts/task-detail';

import { useCreateTimeslotTask } from './use-create-timeslot-task';

const defaultClickDurationMs = 60 * 60 * 1000;

type CalendarSlotMenuState = {
  date: Date;
  timeslot: Timeslot | null;
  anchor: { top: number; left: number };
};

// Drag-select / click opens the slot menu at the cursor; holding Alt bypasses it straight to the New task dialog.
export const useCalendarSlotMenu = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  tags: Tag[],
  smartLists: SmartList[],
) => {
  const openCreateTask = useOpenCreateTask();
  const openCreateEvent = useOpenCreateEventPopup();
  const createTimeslotTask = useCreateTimeslotTask();

  const [slotMenu, setSlotMenu] = useState<CalendarSlotMenuState | null>(null);
  // A plain click fires `select` synchronously and `dateClick` a tick later, so the second
  // handler overwrites the first's state — the calendar handle has to live outside it or
  // the selection highlight survives the menu.
  const calendarApiRef = useRef<CalendarApi | null>(null);

  const openCreateTaskForSlot = useCallback(
    (date: Date, timeslot: Timeslot | null) => {
      openCreateTask(
        {
          timeslot,
          endStrategy: { completionCount: null, deadline: endOfDay(date) },
        },
        { dateAnchored: true },
      );
    },
    [openCreateTask],
  );

  const onSelect = useCallback(
    ({ start, end, jsEvent, view }: DateSelectArg) => {
      calendarApiRef.current = view.calendar;
      const mouseEvent = jsEvent as MouseEvent | null;
      const timeslot: Timeslot = {
        startTime: getCalendarTaskStartTime(start),
        duration: getCalendarTaskDuration(start, end),
      };

      if (mouseEvent?.altKey) {
        openCreateTaskForSlot(start, timeslot);
        view.calendar.unselect();
        return;
      }

      setSlotMenu({
        date: start,
        timeslot,
        anchor: {
          top: mouseEvent?.clientY ?? 0,
          left: mouseEvent?.clientX ?? 0,
        },
      });
    },
    [openCreateTaskForSlot],
  );

  const onDateTime = useCallback(
    (info: {
      date: Date;
      allDay: boolean;
      jsEvent?: MouseEvent;
      view?: { calendar: CalendarApi };
    }) => {
      const { date, allDay, jsEvent, view } = info;
      if (view) {
        calendarApiRef.current = view.calendar;
      }

      const timeslot: Timeslot | null = allDay
        ? null
        : {
            startTime: getCalendarTaskStartTime(date),
            duration: defaultClickDurationMs,
          };

      if (jsEvent?.altKey) {
        openCreateTaskForSlot(date, timeslot);
        return;
      }

      setSlotMenu({
        date,
        timeslot,
        anchor: { top: jsEvent?.clientY ?? 0, left: jsEvent?.clientX ?? 0 },
      });
    },
    [openCreateTaskForSlot],
  );

  const closeSlotMenu = useCallback(() => {
    // clears the FullCalendar selection highlight — `unselectAuto` can't, the popup's
    // backdrop stops the mousedown before it reaches FullCalendar's document listener
    calendarApiRef.current?.unselect();
    setSlotMenu(null);
  }, []);

  const onNewTask = useCallback(() => {
    if (!slotMenu) {
      return;
    }
    openCreateTaskForSlot(slotMenu.date, slotMenu.timeslot);
    closeSlotMenu();
  }, [slotMenu, openCreateTaskForSlot, closeSlotMenu]);

  const onSelectLinkedItem = useCallback(
    (link: { id: ID; type: LinkOptions } | null) => {
      if (!slotMenu || !link) {
        closeSlotMenu();
        return;
      }

      const resolved =
        link.type === LinkOptions.Goal
          ? resolveTimeslotLink(goals, link.id, TaskActionType.GoalTimeslot)
          : resolveTimeslotLink(
              lifeAreas,
              link.id,
              TaskActionType.LifeAreaTimeslot,
            );
      if (resolved) {
        createTimeslotTask(resolved, slotMenu.date, slotMenu.timeslot);
      }
      closeSlotMenu();
    },
    [slotMenu, goals, lifeAreas, createTimeslotTask, closeSlotMenu],
  );

  const onNewEvent = useCallback(() => {
    if (slotMenu) {
      openCreateEvent(
        { date: slotMenu.date, timeslot: slotMenu.timeslot },
        slotMenu.anchor,
      );
    }
    closeSlotMenu();
  }, [slotMenu, openCreateEvent, closeSlotMenu]);

  const onSelectTag = useCallback(
    (tagId: ID) => {
      if (!slotMenu) {
        closeSlotMenu();
        return;
      }
      const link = resolveTimeslotLink(tags, tagId, TaskActionType.TagTimeslot);
      if (link) {
        createTimeslotTask(link, slotMenu.date, slotMenu.timeslot);
      }
      closeSlotMenu();
    },
    [slotMenu, tags, createTimeslotTask, closeSlotMenu],
  );

  const onSelectSmartList = useCallback(
    (smartListId: ID) => {
      if (!slotMenu) {
        closeSlotMenu();
        return;
      }
      const link = resolveTimeslotLink(
        smartLists,
        smartListId,
        TaskActionType.SmartListTimeslot,
      );
      if (link) {
        createTimeslotTask(link, slotMenu.date, slotMenu.timeslot);
      }
      closeSlotMenu();
    },
    [slotMenu, smartLists, createTimeslotTask, closeSlotMenu],
  );

  const slotContextMenu = {
    slot: slotMenu,
    onClose: closeSlotMenu,
    onNewTask,
    onNewEvent,
    onSelectLinkedItem,
    onSelectTag,
    onSelectSmartList,
  };

  return { onSelect, onDateTime, slotContextMenu };
};
